import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Heart, 
  Apple, 
  Dumbbell, 
  Sparkles, 
  RefreshCw,
  Brain,
  Moon,
  Sun,
  Leaf,
  Calendar
} from "lucide-react";
import { GlassCard } from "./GlassCard";
import { Button } from "./ui/button";
import { WellnessProfileWidget } from "./WellnessProfileWidget";
import { CycleDiary } from "./CycleDiary";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tabs for the wellness page
const WELLNESS_TABS = [
  { id: "recommendations", label: "Recomendaciones", icon: Sparkles },
  { id: "cycle", label: "Diario de Ciclo", icon: Calendar }
];

const CATEGORIES = [
  {
    id: "general",
    name: "General",
    icon: Sparkles,
    color: "text-purple-400",
    bgColor: "bg-purple-500/10",
    borderColor: "border-purple-500/30",
    description: "Recomendaciones personalizadas de bienestar integral"
  },
  {
    id: "diet",
    name: "Alimentación",
    icon: Apple,
    color: "text-green-400",
    bgColor: "bg-green-500/10",
    borderColor: "border-green-500/30",
    description: "Nutrición para mejorar tu estado emocional"
  },
  {
    id: "exercise",
    name: "Ejercicio",
    icon: Dumbbell,
    color: "text-orange-400",
    bgColor: "bg-orange-500/10",
    borderColor: "border-orange-500/30",
    description: "Actividades físicas para el bienestar mental"
  },
  {
    id: "hormonal",
    name: "Hormonal",
    icon: Brain,
    color: "text-pink-400",
    bgColor: "bg-pink-500/10",
    borderColor: "border-pink-500/30",
    description: "Equilibrio hormonal según tu perfil"
  }
];

export const WellnessPage = () => {
  const [activeTab, setActiveTab] = useState("recommendations");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [recommendations, setRecommendations] = useState({});
  const [loading, setLoading] = useState({});
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/wellness/profile`);
      setProfile(response.data.profile);
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  const fetchRecommendations = async (category) => {
    setLoading(prev => ({ ...prev, [category]: true }));
    
    try {
      const response = await axios.post(`${API}/wellness/recommendations`, {
        category: category,
        current_mood: null
      });
      
      setRecommendations(prev => ({
        ...prev,
        [category]: {
          content: response.data.recommendations,
          profileUsed: response.data.profile_used,
          isFallback: response.data.is_fallback
        }
      }));
    } catch (error) {
      console.error("Error fetching recommendations:", error);
      toast.error("Error al obtener recomendaciones");
    } finally {
      setLoading(prev => ({ ...prev, [category]: false }));
    }
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category.id);
    if (!recommendations[category.id]) {
      fetchRecommendations(category.id);
    }
  };

  const refreshRecommendations = (categoryId) => {
    fetchRecommendations(categoryId);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="text-center mb-4">
        <h1 className="text-3xl xl:text-4xl font-orbitron text-white flex items-center justify-center gap-3">
          <Heart className="w-8 h-8 xl:w-10 xl:h-10 text-pink-400" />
          Bienestar
        </h1>
        <p className="text-slate-400 mt-2 font-inter">
          Recomendaciones personalizadas para tu bienestar emocional
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex justify-center gap-2">
        {WELLNESS_TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-rajdhani text-sm transition-all flex items-center gap-2 ${
                activeTab === tab.id
                  ? "bg-pink-500/20 border border-pink-500/50 text-pink-400"
                  : "bg-white/5 border border-white/10 text-slate-400 hover:border-white/20 hover:text-white"
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content based on active tab */}
      <AnimatePresence mode="wait">
        {activeTab === "recommendations" ? (
          <motion.div
            key="recommendations"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-6"
          >
            {/* Wellness Profile Widget */}
            <WellnessProfileWidget />

            {/* Category Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {CATEGORIES.map((category) => {
                const Icon = category.icon;
                const isSelected = selectedCategory === category.id;
                const isLoading = loading[category.id];
                
                return (
                  <motion.button
                    key={category.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleCategoryClick(category)}
                    className={`p-4 xl:p-6 rounded-xl border transition-all text-left ${
                      isSelected
                        ? `${category.bgColor} ${category.borderColor} ring-2 ring-offset-2 ring-offset-void ring-opacity-50`
                        : "bg-white/5 border-white/10 hover:bg-white/10"
                    }`}
                    data-testid={`wellness-category-${category.id}`}
                  >
                    <div className={`p-3 rounded-lg ${category.bgColor} w-fit mb-3`}>
                      <Icon className={`w-6 h-6 xl:w-8 xl:h-8 ${category.color}`} />
                    </div>
                    <h3 className={`font-rajdhani font-semibold text-lg ${isSelected ? category.color : 'text-white'}`}>
                      {category.name}
                    </h3>
                    <p className="text-xs text-slate-400 mt-1">
                      {category.description}
                    </p>
                    {isLoading && (
                      <div className="mt-2">
                        <RefreshCw className={`w-4 h-4 ${category.color} animate-spin`} />
                      </div>
                    )}
                  </motion.button>
                );
              })}
            </div>

            {/* Recommendations Display */}
            <AnimatePresence mode="wait">
              {selectedCategory && (
                <motion.div
                  key={selectedCategory}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <GlassCard className="p-6 xl:p-8">
                    {(() => {
                      const category = CATEGORIES.find(c => c.id === selectedCategory);
                      const Icon = category?.icon || Sparkles;
                      const rec = recommendations[selectedCategory];
                      const isLoading = loading[selectedCategory];
                      
                      return (
                        <>
                          <div className="flex items-center justify-between mb-6">
                            <h2 className={`font-orbitron text-xl xl:text-2xl flex items-center gap-3 ${category?.color}`}>
                              <Icon className="w-6 h-6" />
                              {category?.name}
                            </h2>
                            <Button
                              onClick={() => refreshRecommendations(selectedCategory)}
                              disabled={isLoading}
                              variant="outline"
                              size="sm"
                              className="bg-white/5 border-white/20"
                            >
                              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                              Actualizar
                            </Button>
                          </div>

                          {isLoading && !rec ? (
                            <div className="flex flex-col items-center justify-center py-12">
                              <div className="w-12 h-12 border-4 border-pink-500/30 border-t-pink-500 rounded-full animate-spin mb-4" />
                              <p className="text-slate-400 font-rajdhani">
                                Generando recomendaciones personalizadas...
                              </p>
                            </div>
                          ) : rec ? (
                            <div className="space-y-4">
                              {rec.profileUsed && (
                                <div className="flex items-center gap-2 text-xs text-pink-400 mb-4">
                                  <Heart className="w-3 h-3" />
                                  Recomendaciones basadas en tu perfil de bienestar
                                </div>
                              )}
                              
                              <div className="prose prose-invert prose-sm max-w-none">
                                <div className="text-slate-200 leading-relaxed whitespace-pre-wrap font-inter">
                                  <ReactMarkdown
                                    components={{
                                      p: ({ children }) => <p className="mb-3">{children}</p>,
                                      ul: ({ children }) => <ul className="list-none space-y-2 mb-4">{children}</ul>,
                                      li: ({ children }) => <li className="flex items-start gap-2"><span className="text-pink-400">•</span><span>{children}</span></li>,
                                      strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
                                      h1: ({ children }) => <h1 className="text-xl font-orbitron text-white mb-4">{children}</h1>,
                                      h2: ({ children }) => <h2 className="text-lg font-rajdhani text-white mb-3">{children}</h2>,
                                      h3: ({ children }) => <h3 className="text-base font-rajdhani text-slate-200 mb-2">{children}</h3>,
                                    }}
                                  >
                                    {rec.content}
                                  </ReactMarkdown>
                                </div>
                              </div>

                              {rec.isFallback && (
                                <p className="text-xs text-slate-500 mt-4 italic">
                                  Estas son recomendaciones generales. Para consejos más personalizados, completa tu perfil de bienestar.
                                </p>
                              )}
                            </div>
                          ) : (
                            <p className="text-slate-400 text-center py-8">
                              Haz clic en una categoría para ver recomendaciones
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </GlassCard>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Info Card when no category selected */}
            {!selectedCategory && (
              <GlassCard className="p-6 xl:p-8 text-center">
                <div className="flex justify-center gap-4 mb-6">
                  <Sun className="w-8 h-8 text-yellow-400 opacity-50" />
                  <Leaf className="w-8 h-8 text-green-400 opacity-50" />
                  <Moon className="w-8 h-8 text-indigo-400 opacity-50" />
                </div>
                <h3 className="font-orbitron text-lg text-white mb-2">
                  Tu Bienestar, Personalizado
                </h3>
                <p className="text-slate-400 font-inter text-sm max-w-md mx-auto">
                  Selecciona una categoría para recibir recomendaciones de IA basadas en tu perfil, 
                  estado emocional y objetivos de bienestar.
                </p>
                <div className="flex justify-center gap-2 mt-6">
                  <span className="px-3 py-1 rounded-full bg-green-500/10 text-green-400 text-xs">Alimentación</span>
                  <span className="px-3 py-1 rounded-full bg-orange-500/10 text-orange-400 text-xs">Ejercicio</span>
                  <span className="px-3 py-1 rounded-full bg-pink-500/10 text-pink-400 text-xs">Hormonas</span>
                </div>
              </GlassCard>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="cycle"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <CycleDiary />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
